import { useEffect, useRef } from "react";
import type { PlanungAssignment } from "@/lib/montageplanung";
import type { AssignmentDragMode } from "./AssignmentBar";
import { MP_DRAG_THRESHOLD, MP_ROW_SWITCH_Y } from "./layoutConstants";
import { addDays, diffDays, parseIso, toIso } from "./dateUtils";

export interface AssignmentDragState {
  assignment: PlanungAssignment;
  mode: AssignmentDragMode;
  originFrom: string;
  originTo: string;
  originRowId: number;
  startClientX: number;
  startClientY: number;
}

export interface AssignmentDragPreview {
  id: number;
  dateFrom: string;
  dateTo: string;
  rowId: number;
  /** Umschalt beim Verschieben: nur diesen Einsatz bewegen. */
  detachLinked?: boolean;
}

interface UseAssignmentDragOptions {
  active: AssignmentDragState | null;
  days: Date[];
  dayWidth: number;
  trackRefs: React.RefObject<Map<number, HTMLDivElement>>;
  rowIds: number[];
  onPreview: (preview: AssignmentDragPreview | null) => void;
  onCommit: (preview: AssignmentDragPreview) => Promise<void>;
  onClear: () => void;
  /** Ziel für „aus dem Plan in die Ablage“. Nur beim Verschieben. */
  parkElementRef?: React.RefObject<HTMLElement | null>;
  onParkHover?: (hovering: boolean) => void;
  onPark?: (assignment: PlanungAssignment) => Promise<void>;
}

function findRowIdAtY(
  y: number,
  trackRefs: Map<number, HTMLDivElement>,
  rowIds: number[],
): number | null {
  let bestId: number | null = null;
  let bestDist = Infinity;

  for (const rowId of rowIds) {
    const el = trackRefs.get(rowId);
    if (!el) continue;
    const rect = el.getBoundingClientRect();
    const center = rect.top + rect.height / 2;
    const dist = Math.abs(y - center);
    if (y >= rect.top && y <= rect.bottom && dist < bestDist) {
      bestDist = dist;
      bestId = rowId;
    }
  }

  return bestId;
}

function trackLeftForRow(
  rowId: number,
  trackRefs: Map<number, HTMLDivElement>,
): number | null {
  const el = trackRefs.get(rowId);
  if (!el) return null;
  return el.getBoundingClientRect().left;
}

export function useAssignmentDrag({
  active,
  days,
  dayWidth,
  trackRefs,
  rowIds,
  onPreview,
  onCommit,
  onClear,
  parkElementRef,
  onParkHover,
  onPark,
}: UseAssignmentDragOptions) {
  const movedRef = useRef(false);
  const endedRef = useRef(false);
  const onPreviewRef = useRef(onPreview);
  const onCommitRef = useRef(onCommit);
  const onClearRef = useRef(onClear);
  const onParkHoverRef = useRef(onParkHover);
  const onParkRef = useRef(onPark);

  onPreviewRef.current = onPreview;
  onCommitRef.current = onCommit;
  onClearRef.current = onClear;
  onParkHoverRef.current = onParkHover;
  onParkRef.current = onPark;

  useEffect(() => {
    if (!active) return;

    movedRef.current = false;
    endedRef.current = false;
    const lastIdx = days.length - 1;
    const pointerRef = { x: active.startClientX, y: active.startClientY };
    let shiftHeld = false;

    function dayIndexForClientX(clientX: number, rowId: number): number {
      const left = trackLeftForRow(rowId, trackRefs.current ?? new Map());
      if (left == null) return 0;
      return Math.max(0, Math.min(lastIdx, Math.floor((clientX - left) / dayWidth)));
    }

    function targetRowIdFor(clientY: number): number {
      const dy = Math.abs(clientY - active!.startClientY);
      if (dy < MP_ROW_SWITCH_Y) return active!.originRowId;
      const tracks = trackRefs.current ?? new Map();
      return findRowIdAtY(clientY, tracks, rowIds) ?? active!.originRowId;
    }

    function computePreview(
      clientX: number,
      clientY: number,
      detachLinked: boolean,
    ): AssignmentDragPreview | null {
      const targetRowId = targetRowIdFor(clientY);
      const originFrom = parseIso(active!.originFrom);
      const originTo = parseIso(active!.originTo);

      if (active!.mode === "move") {
        const deltaDays = Math.round((clientX - active!.startClientX) / dayWidth);
        const newFrom = addDays(originFrom, deltaDays);
        const newTo = addDays(originTo, deltaDays);
        return {
          id: active!.assignment.id,
          dateFrom: toIso(newFrom),
          dateTo: toIso(newTo),
          rowId: targetRowId,
          detachLinked,
        };
      }

      const idx = dayIndexForClientX(clientX, active!.originRowId);
      const anchor = days[idx]!;

      if (active!.mode === "resize-start") {
        const maxFrom = originTo;
        const newFrom = parseIso(toIso(anchor)) <= maxFrom ? anchor : maxFrom;
        return {
          id: active!.assignment.id,
          dateFrom: toIso(newFrom),
          dateTo: active!.originTo,
          rowId: active!.originRowId,
        };
      }

      const minTo = originFrom;
      const newTo = parseIso(toIso(anchor)) >= minTo ? anchor : minTo;
      return {
        id: active!.assignment.id,
        dateFrom: active!.originFrom,
        dateTo: toIso(newTo),
        rowId: active!.originRowId,
      };
    }

    function hasExceededDragThreshold(clientX: number, clientY: number): boolean {
      const dx = clientX - active!.startClientX;
      const dy = clientY - active!.startClientY;
      return Math.hypot(dx, dy) >= MP_DRAG_THRESHOLD;
    }

    function isOverPark(clientX: number, clientY: number): boolean {
      if (active!.mode !== "move") return false;
      const el = parkElementRef?.current;
      if (!el) return false;
      const rect = el.getBoundingClientRect();
      return (
        clientX >= rect.left &&
        clientX <= rect.right &&
        clientY >= rect.top &&
        clientY <= rect.bottom
      );
    }

    function publishPreview(clientX: number, clientY: number, detachLinked: boolean) {
      pointerRef.x = clientX;
      pointerRef.y = clientY;
      shiftHeld = detachLinked;
      if (!hasExceededDragThreshold(clientX, clientY)) {
        onParkHoverRef.current?.(false);
        onPreviewRef.current(null);
        return;
      }
      movedRef.current = true;
      if (isOverPark(clientX, clientY)) {
        onParkHoverRef.current?.(true);
        onPreviewRef.current(null);
        return;
      }
      onParkHoverRef.current?.(false);
      onPreviewRef.current(computePreview(clientX, clientY, detachLinked));
    }

    function onPointerMove(e: PointerEvent) {
      if (endedRef.current) return;
      publishPreview(e.clientX, e.clientY, e.shiftKey);
    }

    function onShiftKey(e: KeyboardEvent) {
      if (e.key !== "Shift" || e.repeat || endedRef.current) return;
      const held = e.type === "keydown";
      if (held === shiftHeld) return;
      publishPreview(pointerRef.x, pointerRef.y, held);
    }

    function onWindowBlur() {
      if (!shiftHeld || endedRef.current) return;
      publishPreview(pointerRef.x, pointerRef.y, false);
    }

    async function endPointerDrag(clientX: number, clientY: number, detachLinked: boolean) {
      if (endedRef.current) return;
      endedRef.current = true;
      const exceeded = movedRef.current || hasExceededDragThreshold(clientX, clientY);
      const parking = exceeded && isOverPark(clientX, clientY);
      const preview = exceeded && !parking ? computePreview(clientX, clientY, detachLinked) : null;

      try {
        if (parking && onParkRef.current) {
          await onParkRef.current(active!.assignment);
        } else if (exceeded && preview) {
          const unchanged =
            preview.dateFrom === active!.originFrom &&
            preview.dateTo === active!.originTo &&
            preview.rowId === active!.originRowId;
          if (!unchanged) {
            await onCommitRef.current(preview);
          }
        }
      } finally {
        onParkHoverRef.current?.(false);
        onPreviewRef.current(null);
        onClearRef.current();
      }
    }

    function onPointerUp(e: PointerEvent) {
      void endPointerDrag(e.clientX, e.clientY, e.shiftKey);
    }

    document.addEventListener("pointermove", onPointerMove);
    document.addEventListener("pointerup", onPointerUp);
    document.addEventListener("pointercancel", onPointerUp);
    document.addEventListener("keydown", onShiftKey);
    document.addEventListener("keyup", onShiftKey);
    window.addEventListener("blur", onWindowBlur);
    document.body.classList.add("mp-dragging");

    return () => {
      document.removeEventListener("pointermove", onPointerMove);
      document.removeEventListener("pointerup", onPointerUp);
      document.removeEventListener("pointercancel", onPointerUp);
      document.removeEventListener("keydown", onShiftKey);
      document.removeEventListener("keyup", onShiftKey);
      window.removeEventListener("blur", onWindowBlur);
      document.body.classList.remove("mp-dragging");
    };
  }, [active, days, dayWidth, rowIds, trackRefs, parkElementRef]);
}

export function assignmentGeom(
  dateFrom: string,
  dateTo: string,
  windowFrom: Date,
  lastIdx: number,
) {
  const start = diffDays(windowFrom, parseIso(dateFrom));
  const end = diffDays(windowFrom, parseIso(dateTo));
  if (end < 0 || start > lastIdx) return null;
  const clipLeft = start < 0;
  const clipRight = end > lastIdx;
  const startIdx = Math.max(0, start);
  const endIdx = Math.min(lastIdx, end);
  return { startIdx, span: endIdx - startIdx + 1, clipLeft, clipRight };
}
